import type { IconName } from '@starter/ui';
import { useCatalog } from '../catalogs/use-catalog.ts';
import { Launcher, LauncherRow } from '../jobs/launcher.tsx';

/**
 * The video flows, as doors — one list behind three surfaces.
 *
 * The home page, the hub and the «Create» dialog all offer the same three, and
 * they were three copies until this file: the day the showcase became
 * orderable, two of the copies said so and the third went on showing a door
 * that could not be pressed. Which of them open is a row in `job_kinds`, so
 * the answer belongs in one place and the prefix is the only thing a surface
 * gets to choose.
 */
const ICONS: Record<string, IconName> = {
  ugc_video: 'video-ads',
  showcase_video: 'box',
  avatar_slideshow: 'images',
};

/**
 * Whether video is part of the product at all today.
 *
 * A different question from `orderable`, which asks whether a flow *can* be
 * made: this asks whether it is on offer. The owner hid video on 2026-09-25 so
 * that image ads get the attention, and it is a row rather than a constant for
 * the usual reason — the day it returns is a migration, not a deploy. The
 * original carries the same field on its own flows.
 *
 * Read as «any video kind is visible» rather than as a flag of its own,
 * because the rail's destination and the home page's section exist to hold
 * these kinds and nothing else. Hide the last one and there is nothing behind
 * the door.
 */
export function useVideoOffered(): boolean {
  // Note what this does *not* gate: the doors themselves. Hiding is about the
  // ways in — the rail, the home page, the «Create» dialog — and `/video-ads`
  // keeps working for anyone who has the address. A section that stops
  // functioning is removed, not hidden, and removing it is not what was asked.
  const kinds = useCatalog('job_kinds');
  return kinds.items.some((item) => item.produces === 'video' && item.visible !== false);
}

export function VideoDoors({ testIdPrefix }: { testIdPrefix: string }) {
  const kinds = useCatalog('job_kinds');

  return (
    <LauncherRow>
      {kinds.items
        .filter((item) => item.produces === 'video')
        .map((item) => {
          const orderable = item.orderable === true;
          const hint = kinds.hint(String(item.key));
          const whyNot = (item.whyNot as { en?: string } | undefined)?.en;

          return (
            <Launcher
              // A door that cannot be pressed says what it is waiting for
              // rather than what it would have done.
              description={orderable ? hint : (whyNot ?? hint)}
              disabled={!orderable}
              icon={ICONS[item.key] ?? 'video-ads'}
              key={item.key}
              search={{
                kind: item.key,
                productId: undefined,
                brandId: undefined,
                still: undefined,
                template: undefined,
              }}
              artKey={item.key}
              testId={`${testIdPrefix}${item.key.replace(/_/g, '-')}`}
              title={kinds.label(item.key)}
              to={orderable ? '/video-ad' : undefined}
            />
          );
        })}
    </LauncherRow>
  );
}
